import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { AlertTriangle, ArrowLeftRight, Compass, Loader2, Lock } from "lucide-react";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import type { Id } from "../../../../convex/_generated/dataModel";
import { interpreterCorrelation } from "../../../../convex/referentiel/rapport";
import { COULEUR_VERDICT, formatR } from "./RapportMatchingCulturel";
import {
  useMatchingReferentiel,
  type EvaluationFitCulturelRow,
  type EvaluationPamiRow,
} from "./useMatchingReferentiel";
import { dateCourte } from "@repo/utils";

export type { EvaluationFitCulturelRow, EvaluationPamiRow };

type Props = {
  campagneId: Id<"campagnes">;
  /** Référentiel contre lequel comparer — celui du test sélectionné. */
  cible: "pami" | "pc-fit";
  evaluationsPami: EvaluationPamiRow[] | undefined;
  evaluationsFitCulturel: EvaluationFitCulturelRow[] | undefined;
  /** Lien qui ouvre la fiche d'un candidat (panneau), sur ce matching. */
  hrefCandidat: (parcoursId: string) => string;
};

/** Nombre de candidats affichés par défaut dans le classement. Au-delà, la
 *  lecture ligne à ligne n'apporte plus rien : le tableau sert à repérer le
 *  haut du classement, pas à parcourir toute la campagne. */
const TOP_CLASSEMENT = 5;

/**
 * Matching candidat ↔ référentiel entreprise (plan §8.1).
 *
 * Le score n'est JAMAIS stocké : il est recalculé à la lecture contre une
 * projection du référentiel. C'est ce qui garantit que tous les candidats de la
 * campagne sont comparés au même référentiel — et que le classement ci-dessous
 * est légitime (§4.1). Corollaire : tout score est rendu avec l'identité de la
 * version qui l'a produit.
 *
 * Le calcul tourne côté front (§5.5), comme les scores PAMI : `computePamiScores`
 * vit dans `@repo/utils`, dont Convex évite la dépendance.
 */
export function ReferentielMatchingTab({
  campagneId,
  cible,
  evaluationsPami,
  evaluationsFitCulturel,
  hrefCandidat,
}: Props) {
  const naviguer = useNavigate();
  const [versionChoisie, setVersionChoisie] = useState<number | null>(null);
  const [toutAfficher, setToutAfficher] = useState(false);

  const matching = useMatchingReferentiel({
    campagneId,
    cible,
    evaluationsPami,
    evaluationsFitCulturel,
    versionChoisie,
  });
  const { etat, versions, version, echelle, classement } = matching;

  /**
   * Le tableau n'affiche que le haut du classement : au-delà, la liste devient
   * un mur de lignes sans valeur de lecture. Le détail d'un candidat se lit
   * dans sa fiche.
   */
  const classementAffiche = useMemo(
    () => (toutAfficher ? classement : classement.slice(0, TOP_CLASSEMENT)),
    [classement, toutAfficher],
  );

  if (matching.chargement || etat === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="text-primary h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (etat === null) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-8 text-center text-sm">
          Aucun référentiel n'est configuré sur cette campagne. Activez-le depuis
          l'onglet « Référentiel » pour comparer les candidats aux attentes de
          l'organisation.
        </CardContent>
      </Card>
    );
  }

  if (!version) {
    return (
      <Card>
        <CardContent className="text-muted-foreground py-8 text-center text-sm">
          Aucun référent n'a encore contribué : il n'y a pas de référentiel
          contre lequel comparer les candidats.
        </CardContent>
      </Card>
    );
  }

  const estVersionCourante = version.version === versions.length;
  const itemsPersonnalises = etat.config.dimensions.some(
    (d) => d.origine === "custom",
  );

  return (
    <div className="space-y-6">
      {/* Identité de la version — à côté de TOUT score, exports compris (§4.1). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-3 text-base">
            <Compass className="h-4 w-4" />
            Matching au référentiel
            <Badge variant="info">
              v{version.version} · {version.nbReferents} référent
              {version.nbReferents > 1 ? "s" : ""} ·{" "}
              {dateCourte(version.computedAt)}
            </Badge>
            {version.fige && (
              <Badge variant="purple">
                <Lock className="mr-1 h-3 w-3" />
                Figé
              </Badge>
            )}
            {itemsPersonnalises && (
              <Badge variant="outline">Items personnalisés</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {versions.length > 1 && (
            // Plus un <label> : un declencheur Radix n'est pas un controle
            // labelable, le rattachement passe par `aria-label`.
            <div className="flex flex-wrap items-center gap-2">
              <ArrowLeftRight className="text-muted-foreground h-4 w-4" />
              <span>Comparer à la version</span>
              <Select
                value={String(version.version)}
                onValueChange={(valeur) => setVersionChoisie(Number(valeur))}
              >
                <SelectTrigger
                  aria-label="Comparer à la version"
                  className="w-auto min-w-56"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...versions].reverse().map((v) => (
                    <SelectItem key={v.version} value={String(v.version)}>
                      v{v.version} — {v.nbReferents} référent
                      {v.nbReferents > 1 ? "s" : ""} · {dateCourte(v.computedAt)}
                      {v.version === versions.length ? " (courante)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {!estVersionCourante && (
            <div className="flex gap-2 rounded-surface border border-etat-alerte-trait bg-etat-alerte-surface p-3 text-etat-alerte-contenu">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <p>
                Vous lisez une version antérieure du référentiel. Les scores
                affichés ne sont pas ceux de la version courante (v
                {versions.length}).
              </p>
            </div>
          )}

          {!version.fige && (
            <p className="text-muted-foreground text-xs">
              Le référentiel est encore ouvert : une contribution tardive fera
              bouger tous les scores en même temps. Figez-le avant d'arbitrer.
            </p>
          )}

          {itemsPersonnalises && (
            <p className="text-muted-foreground text-xs">
              Ce référentiel comporte des items personnalisés : les scores ne
              sont comparables qu'entre candidats de cette campagne.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Classement — INTRA-campagne uniquement (§11.2). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Classement des candidats{" "}
            {toutAfficher || classement.length <= TOP_CLASSEMENT
              ? `(${classement.length})`
              : `(${TOP_CLASSEMENT} meilleurs sur ${classement.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {classement.length === 0 ? (
            <p className="text-muted-foreground p-6 text-center text-sm">
              Aucune évaluation PAMI dans cette campagne.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      #
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      Candidat
                    </th>
                    <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                      {echelle
                        ? "Score de matching"
                        : "Corrélation moyenne (r)"}
                    </th>
                    {/* Pas de colonne d'interprétation en PAMI : le score y est
                        la moyenne pondérée des dimensions atteintes, il se
                        lit directement. En fit-culturel, c'est le verdict du
                        rapport — un r non significatif se lit « Pas de
                        tendance nette ». */}
                    {!echelle && (
                      <th className="text-muted-foreground px-4 py-2 text-left font-medium">
                        Interprétation
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {classementAffiche.map((entree) => {
                    const index = classement.indexOf(entree);
                    const score = entree.comparaison?.scoreMatching ?? null;
                    const indicateur = echelle
                      ? null
                      : interpreterCorrelation(
                          entree.comparaison?.correlation ?? null,
                        );
                    return (
                      <tr
                        key={entree.row._id}
                        // La ligne entière ouvre la fiche ; le nom en est le
                        // vrai lien, pour le clavier et l'ouverture en onglet.
                        onClick={() =>
                          void naviguer(hrefCandidat(entree.row.parcoursId))
                        }
                        className="cursor-pointer border-b last:border-0 hover:bg-muted/40"
                      >
                        <td className="text-muted-foreground px-4 py-2 tabular-nums">
                          {score === null ? "—" : index + 1}
                        </td>
                        <td className="px-4 py-2 font-medium">
                          <Link
                            to={hrefCandidat(entree.row.parcoursId)}
                            onClick={(e) => e.stopPropagation()}
                            className="hover:underline"
                          >
                            {entree.row.prenomCandidat} {entree.row.nomCandidat}
                          </Link>
                        </td>
                        <td className="px-4 py-2">
                          {score === null ? (
                            <span className="text-muted-foreground">
                              {echelle
                                ? "PAMI non exploitable"
                                : "Passation incomplète"}
                            </span>
                          ) : indicateur ? (
                            <div className="flex items-center gap-2">
                              <span className="w-12 tabular-nums">
                                {formatR(score)}
                              </span>
                              {/* Axe [-1, 1] centré sur 0 (le hasard) : la
                                  barre part du centre vers r, comme l'axe du
                                  rapport. */}
                              <div className="relative h-2 w-24 overflow-hidden rounded-full bg-muted">
                                <div className="absolute inset-y-0 left-1/2 w-px bg-foreground/30" />
                                <div
                                  className="absolute inset-y-0 rounded-full"
                                  style={{
                                    left: `${50 + Math.min(0, score) * 50}%`,
                                    width: `${Math.max(1, Math.abs(score) * 50)}%`,
                                    backgroundColor:
                                      COULEUR_VERDICT[indicateur.sens],
                                  }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="w-8 tabular-nums">
                                {Math.round(score)}
                              </span>
                              <div className="relative h-2 w-24 overflow-hidden rounded-full bg-muted">
                                <div
                                  className="h-full rounded-full bg-primary"
                                  style={{
                                    width: `${Math.max(1, Math.min(100, score))}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        {indicateur && (
                          <td className="text-muted-foreground px-4 py-2">
                            {score === null
                              ? "—"
                              : indicateur.libelle}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {classement.length > TOP_CLASSEMENT && (
                <div className="border-t p-3 text-center">
                  <button
                    type="button"
                    onClick={() => setToutAfficher((v) => !v)}
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    {toutAfficher
                      ? `Afficher seulement les ${TOP_CLASSEMENT} meilleurs`
                      : `Afficher les ${classement.length} candidats`}
                  </button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>


    </div>
  );
}
